import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, FileText, Link2, Copy, Trash2, Eye, EyeOff,
  GripVertical, QrCode, ExternalLink, Users, X, Pencil, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
];

const LEAD_FIELD_MAP = [
  { value: 'none', label: 'None (custom)' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'company', label: 'Company' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'notes', label: 'Notes' },
];

function generateFieldId() {
  return 'field_' + Math.random().toString(36).slice(2, 8);
}

// ─── Form Builder Modal ───
function FormBuilderModal({ open, onClose, onSaved, editingForm }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [submitText, setSubmitText] = useState('Submit');
  const [successMsg, setSuccessMsg] = useState("Thank you! We'll be in touch soon.");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingForm) {
      setName(editingForm.name || '');
      setDescription(editingForm.description || '');
      // Convert empty maps_to to 'none' for Select component
      const fieldsWithNone = (editingForm.fields || []).map(f => ({
        ...f,
        maps_to: f.maps_to || 'none'
      }));
      setFields(fieldsWithNone);
      setSubmitText(editingForm.submit_button_text || 'Submit');
      setSuccessMsg(editingForm.success_message || "Thank you! We'll be in touch soon.");
    } else {
      setName('');
      setDescription('');
      setFields([
        { id: 'first_name', label: 'First Name', type: 'text', required: true, placeholder: 'John', options: [], maps_to: 'first_name' },
        { id: 'last_name', label: 'Last Name', type: 'text', required: false, placeholder: 'Smith', options: [], maps_to: 'last_name' },
        { id: 'email', label: 'Email', type: 'email', required: true, placeholder: 'john@example.com', options: [], maps_to: 'email' },
        { id: 'phone', label: 'Phone', type: 'phone', required: false, placeholder: '+1 (555) 123-4567', options: [], maps_to: 'phone' },
        { id: 'company', label: 'Company', type: 'text', required: false, placeholder: 'Acme Inc', options: [], maps_to: 'company' },
      ]);
      setSubmitText('Submit');
      setSuccessMsg("Thank you! We'll be in touch soon.");
    }
  }, [editingForm, open]);

  const addField = () => {
    setFields(prev => [...prev, {
      id: generateFieldId(), label: 'New Field', type: 'text',
      required: false, placeholder: '', options: [], maps_to: 'none'
    }]);
  };

  const updateField = (idx, key, value) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: value } : f));
  };

  const removeField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
  };

  const moveField = (idx, dir) => {
    const newFields = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= newFields.length) return;
    [newFields[idx], newFields[target]] = [newFields[target], newFields[idx]];
    setFields(newFields);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Form name is required'); return; }
    if (fields.length === 0) { toast.error('Add at least one field'); return; }
    setSaving(true);
    try {
      // Convert 'none' maps_to back to empty string for API
      const cleanedFields = fields.map(f => ({
        ...f,
        maps_to: f.maps_to === 'none' ? '' : f.maps_to
      }));
      const payload = { name, description, fields: cleanedFields, submit_button_text: submitText, success_message: successMsg };
      if (editingForm) {
        const { data } = await axios.put(`${API}/api/leads/forms/${editingForm.id}`, payload);
        toast.success('Form updated');
        onSaved(data, true);
      } else {
        const { data } = await axios.post(`${API}/api/leads/forms`, payload);
        toast.success('Form created');
        onSaved(data, false);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save form');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            {editingForm ? 'Edit Form' : 'Create Capture Form'}
          </DialogTitle>
          <DialogDescription>Build a form that feeds leads directly into your pipeline</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Form Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Contact Us" data-testid="form-name-input" />
            </div>
            <div>
              <Label>Submit Button Text</Label>
              <Input value={submitText} onChange={e => setSubmitText(e.target.value)} placeholder="Submit" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief form description shown to visitors" rows={2} />
          </div>
          <div>
            <Label>Success Message</Label>
            <Input value={successMsg} onChange={e => setSuccessMsg(e.target.value)} placeholder="Thank you!" />
          </div>

          {/* Field Builder */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Form Fields</Label>
              <Button type="button" variant="outline" size="sm" onClick={addField} className="h-7 text-xs gap-1" data-testid="add-field-btn">
                <Plus className="h-3 w-3" /> Add Field
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((field, idx) => (
                <div key={field.id} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border" data-testid={`form-field-${idx}`}>
                  <div className="flex flex-col gap-1 mt-1">
                    <button onClick={() => moveField(idx, -1)} className="text-gray-400 hover:text-gray-600 disabled:opacity-30" disabled={idx === 0}><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={() => moveField(idx, 1)} className="text-gray-400 hover:text-gray-600 disabled:opacity-30" disabled={idx === fields.length - 1}><ChevronDown className="h-3 w-3" /></button>
                  </div>
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Input value={field.label} onChange={e => updateField(idx, 'label', e.target.value)} placeholder="Label" className="text-xs h-8" />
                    <Select value={field.type} onValueChange={v => updateField(idx, 'type', v)}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={field.maps_to || ''} onValueChange={v => updateField(idx, 'maps_to', v)}>
                      <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Maps to..." /></SelectTrigger>
                      <SelectContent>{LEAD_FIELD_MAP.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Switch checked={field.required} onCheckedChange={v => updateField(idx, 'required', v)} className="h-4 w-7" />
                        <span className="text-[10px] text-gray-500">Req</span>
                      </div>
                      <button onClick={() => removeField(idx)} className="text-red-400 hover:text-red-600 ml-auto"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-form-btn">
            {saving ? 'Saving...' : editingForm ? 'Update Form' : 'Create Form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── QR Code Dialog ───
function QRDialog({ open, onClose, url, formName }) {
  const copyUrl = () => { navigator.clipboard.writeText(url); toast.success('URL copied!'); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-blue-500" /> QR Code</DialogTitle>
          <DialogDescription>{formName}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="bg-white p-4 rounded-xl border-2 border-gray-100" data-testid="qr-code-display">
            <QRCodeSVG value={url} size={200} level="H" includeMargin={true} />
          </div>
          <div className="flex items-center gap-2 w-full">
            <Input value={url} readOnly className="text-xs flex-1" />
            <Button variant="outline" size="icon" onClick={copyUrl} className="flex-shrink-0" data-testid="copy-form-url-btn">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-400 text-center">Scan this QR code or share the URL to collect leads</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Main Page ───
export default function LeadCaptureFormsPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [qrForm, setQrForm] = useState(null);

  const fetchForms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/leads/forms`);
      setForms(data);
    } catch { toast.error('Failed to load forms'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const handleSaved = (form, isUpdate) => {
    if (isUpdate) {
      setForms(prev => prev.map(f => f.id === form.id ? { ...f, ...form } : f));
    } else {
      setForms(prev => [{ ...form, submission_count: 0 }, ...prev]);
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Delete this form? Existing leads will remain.')) return;
    try {
      await axios.delete(`${API}/api/leads/forms/${formId}`);
      setForms(prev => prev.filter(f => f.id !== formId));
      toast.success('Form deleted');
    } catch { toast.error('Failed to delete form'); }
  };

  const toggleActive = async (form) => {
    try {
      await axios.put(`${API}/api/leads/forms/${form.id}`, { active: !form.active });
      setForms(prev => prev.map(f => f.id === form.id ? { ...f, active: !f.active } : f));
      toast.success(form.active ? 'Form deactivated' : 'Form activated');
    } catch { toast.error('Failed to update form'); }
  };

  const getFormUrl = (slug) => `${window.location.origin}/form/${slug}`;

  const copyUrl = (slug) => {
    navigator.clipboard.writeText(getFormUrl(slug));
    toast.success('Form URL copied!');
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="lead-capture-forms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Lead Capture Forms<PageInfoTooltip pageKey="capture-forms" /></h1>
            <p className="text-xs text-gray-400">Build forms that feed leads into your pipeline</p>
          </div>
        </div>
        <Button onClick={() => { setEditingForm(null); setBuilderOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9 gap-1.5" data-testid="create-form-btn">
          <Plus className="h-4 w-4" /> Create Form
        </Button>
      </div>

      {/* Forms List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
        </div>
      ) : forms.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No forms yet</h3>
            <p className="text-sm text-gray-500 mb-4">Create your first lead capture form to start collecting leads</p>
            <Button onClick={() => { setEditingForm(null); setBuilderOpen(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              <Plus className="h-4 w-4" /> Create Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map(form => (
            <Card key={form.id} className="border-gray-200 hover:shadow-md transition-shadow" data-testid={`form-card-${form.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-gray-900 truncate">{form.name}</h3>
                      <Badge className={form.active ? 'bg-green-100 text-green-700 text-[9px]' : 'bg-gray-100 text-gray-500 text-[9px]'}>
                        {form.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {form.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{form.description}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {form.fields?.length || 0} fields</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {form.submission_count || 0} submissions</span>
                </div>

                {/* URL Row */}
                <div className="flex items-center gap-1.5 bg-gray-50 rounded-md px-2 py-1.5 mb-3">
                  <Link2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="text-[10px] text-gray-500 truncate flex-1">/form/{form.slug}</span>
                  <button onClick={() => copyUrl(form.slug)} className="text-blue-500 hover:text-blue-600" data-testid={`copy-url-${form.id}`}><Copy className="h-3 w-3" /></button>
                  <a href={getFormUrl(form.slug)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600"><ExternalLink className="h-3 w-3" /></a>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => { setEditingForm(form); setBuilderOpen(true); }} data-testid={`edit-form-${form.id}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => setQrForm(form)} data-testid={`qr-form-${form.id}`}>
                    <QrCode className="h-3 w-3" /> QR
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => toggleActive(form)} data-testid={`toggle-form-${form.id}`}>
                    {form.active ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(form.id)} data-testid={`delete-form-${form.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Builder Modal */}
      <FormBuilderModal
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditingForm(null); }}
        onSaved={handleSaved}
        editingForm={editingForm}
      />

      {/* QR Code Dialog */}
      {qrForm && (
        <QRDialog
          open={!!qrForm}
          onClose={() => setQrForm(null)}
          url={getFormUrl(qrForm.slug)}
          formName={qrForm.name}
        />
      )}
    </div>
  );
}
